import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable";
import useDepartment from "../../../core/api/useDepartment";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../router/all_routes";
import axios from "axios";
import { Modal } from "antd";
import { useAuthToken } from "../../../core/data/redux/useAuthToken";
import { usePermission } from "../../../core/api/useRolePermission";

const DepartmentSchema = Yup.object().shape({
  department_name: Yup.string().required("Department name is required"),
  status: Yup.string().required("Status is required"),
});

const Department = () => {
  const empToken = useAuthToken();
  const {
    departments,
    isLoading,
    fetchDepartments,
    addDepartment,
    updateDepartment,
    deleteDepartment,
  } = useDepartment(empToken);

  const [selectedDept, setSelectedDept] = useState<any | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  // Permissions
  const canCreate = usePermission("Departments", "create");
  const canUpdate = usePermission("Departments", "write");
  const canDelete = usePermission("Departments", "delete");
  const canRead = usePermission("Departments", "read");

  useEffect(() => {
    fetchDepartments();
  }, []);

  const handleAdd = async (values: any, { setSubmitting, resetForm }: any) => {
    try {
      await addDepartment(values);
      fetchDepartments();
      resetForm();
      document.getElementById("add_dept_close")?.click();
    } catch (err: unknown) {
      console.error("Add department error:", err);
    }
  };

  const handleEdit = async (values: any, { setSubmitting }: any) => {
    try {
      if (selectedDept?.department_name) {
        await updateDepartment(
          selectedDept.department_name.toLowerCase(),
          values
        );
        fetchDepartments();
        document.getElementById("edit_dept_close")?.click();
      }
    } catch (err: any) {
      const details = err?.response?.data?.details || "";

      if (
        err?.response?.data?.code === "DATABASE_ERROR" &&
        details.includes("Cannot delete or update a parent row")
      ) {
        toast.warning(
          "Cannot update: This department is linked to existing designations or employees."
        );
      } else {
        console.error("Failed to update department");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (record: any) => {
    if (!canDelete) {
      toast.warning("You don't have permission to delete departments.");
      return;
    }
    setSelectedDept(record);
    setDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    try {
      if (selectedDept?.department_name) {
        await deleteDepartment(selectedDept.department_name.toLowerCase());
        fetchDepartments();
        setDeleteModalVisible(false);
        setSelectedDept(null);
      }
    } catch (err: any) {
      console.error("Delete department error:", err);

      const details = err?.response?.data?.details || "";

      if (
        err?.response?.data?.code === "UNEXPECTED_ERROR" &&
        details.includes("cannot be deleted because it is currently assigned")
      ) {
        toast.warning(
          "Cannot delete: This department is assigned to designations or employees."
        );
      } else {
        let errorMessage = "Error deleting department. Please try again.";

        if (axios.isAxiosError(err)) {
          const { response } = err;
          if (response) {
            const { data } = response;
            errorMessage = data?.details || data?.message || errorMessage;
          } else if (err.request) {
            errorMessage =
              "No response from server. Please check your network.";
          } else {
            errorMessage = err.message;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }
        console.error(errorMessage);
      }
    }
  };

  const columns = [
    {
      title: "S.No",
      key: "serial",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Department",
      dataIndex: "department_name",
    },
    {
      title: "Employees",
      dataIndex: "employee_count",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => {
        const lower = (text || "").toLowerCase();
        return (
          <span
            className={`badge d-inline-flex align-items-center badge-xs ${
              lower === "active" ? "badge-success" : "badge-danger"
            }`}
          >
            <i className="ti ti-point-filled me-1"></i>
            {text
              ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
              : "Unknown"}
          </span>
        );
      },
    },
    {
      title: "Actions",
      render: (_: any, record: any) => (
        <div className="action-icon d-inline-flex">
          <button
            className="btn btn-outline-light p-1 me-2"
            onClick={(e) => {
              if (!canUpdate) {
                toast.warning("You don't have permission to edit departments.");
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              setSelectedDept(record);
            }}
            data-bs-toggle={canUpdate ? "modal" : undefined}
            data-bs-target={canUpdate ? "#edit_dept" : undefined}
          >
            <i className="ti ti-edit" />
          </button>
          <button
            className="btn btn-outline-light p-1"
            onClick={(e) => {
              e.preventDefault();
              handleDeleteClick(record);
            }}
          >
            <i className="ti ti-trash" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Departments</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active">Department</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center flex-wrap">
            <button
              type="button"
              className="btn btn-primary"
              onClick={(e) => {
                if (!canCreate) {
                  toast.warning(
                    "You don't have permission to create departments."
                  );
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
              }}
              data-bs-toggle={canCreate ? "modal" : undefined}
              data-bs-target={canCreate ? "#add_dept" : undefined}
            >
              <i className="ti ti-circle-plus me-2" /> Add Department
            </button>
            <div className="ms-2">
              <CollapseHeader />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-body p-0">
            {isLoading ? (
              <p className="p-3">Loading...</p>
            ) : (
              <Table
                dataSource={(departments || []).map((item: any) => ({
                  ...item,
                  key: item.department_name,
                }))}
                columns={columns}
              />
            )}
          </div>
        </div>
      </div>

      {/* Add Modal */}
      <div className="modal fade" id="add_dept">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Department</h4>
              <button
                type="button"
                className="btn-close"
                id="add_dept_close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <Formik
              initialValues={{ department_name: "", status: "Active" }}
              validationSchema={DepartmentSchema}
              onSubmit={handleAdd}
            >
              {({ isSubmitting, errors, touched, resetForm }) => (
                <Form>
                  <div className="modal-body pb-0">
                    <div className="mb-3">
                      <label className="form-label">Department Name</label>
                      <Field name="department_name" className="form-control" />
                      {errors.department_name && touched.department_name && (
                        <div className="text-danger">
                          {errors.department_name as string}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <Field as="select" name="status" className="form-control">
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </Field>
                      {errors.status && touched.status && (
                        <div className="text-danger">
                          {errors.status as string}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-light me-2"
                      onClick={() => resetForm()}
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      Add Department
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <div className="modal fade" id="edit_dept">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Department</h4>
              <button
                type="button"
                className="btn-close"
                id="edit_dept_close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <Formik
              enableReinitialize
              initialValues={{
                department_name: selectedDept?.department_name || "",
                status: selectedDept?.status
                  ? selectedDept.status.charAt(0).toUpperCase() +
                    selectedDept.status.slice(1).toLowerCase()
                  : "Active",
              }}
              validationSchema={DepartmentSchema}
              onSubmit={handleEdit}
            >
              {({ isSubmitting, errors, touched, resetForm }) => (
                <Form>
                  <div className="modal-body pb-0">
                    <div className="mb-3">
                      <label className="form-label">Department Name</label>
                      <Field name="department_name" className="form-control" />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <Field as="select" name="status" className="form-control">
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </Field>
                      {errors.status && touched.status && (
                        <div className="text-danger">
                          {errors.status as string}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-light me-2"
                      onClick={() => resetForm()}
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      Save Changes
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={deleteModalVisible}
        onOk={() => {
          if (!canDelete) {
            toast.warning("You don't have permission to delete departments.");
            return;
          }
          handleDelete();
          setDeleteModalVisible(false);
        }}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{selectedDept?.department_name}</strong>?
        </p>
      </Modal>
    </div>
  );
};

export default Department;
